import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { canEditRum, type RumDetailView } from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { errorMessage } from '@/shared/http/http-client';
import { Button, EmptyState, ErrorText, Kicker, Muted, Skeleton } from '@/shared/ui';
import { RumFields, useRumFields } from '@/shared/rum/RumFields';
import styles from '@/shared/rum/rum-fields.module.scss';

/**
 * Correction d'une fiche de rhum (US-07).
 *
 * Une fiche se crée en séance, debout, une bouteille dans la main : le degré exact et
 * l'âge se retrouvent le lendemain, sur l'étiquette ou sur le site de l'embouteilleur. Sans
 * cet écran, l'API savait déjà accepter la correction (`PATCH /rums/:id`) mais rien ne
 * l'appelait, et une fiche incomplète le restait pour toujours.
 *
 * Le formulaire est monté en deux temps — la fiche doit être lue avant que `useRumFields`
 * puisse s'initialiser, et un hook ne se préremplit pas après coup sans se battre contre
 * lui-même. Le composant interne reçoit donc la fiche déjà chargée.
 */
export function EditRumScreen(): ReactNode {
  const { id = '' } = useParams();
  const api = useApi();
  const rum = useAsync(() => api.rums.detail(id), [id]);
  const { member } = useApp();

  if (rum.loading) return <Skeleton />;
  if (rum.error || !rum.data) return <EmptyState>{rum.error ?? 'Fiche introuvable.'}</EmptyState>;

  const data = rum.data;

  /*
   * La même porte que sur la fiche, refermée ici aussi : l'écran est atteignable par son
   * URL, et un membre qui la tape à la main ne doit pas remplir un formulaire pour se
   * heurter à un 403 à l'enregistrement. L'API le refuserait de toute façon — c'est elle
   * qui décide —, mais le lui dire tout de suite est la moindre des politesses.
   */
  const allowed =
    member != null && canEditRum(member.role, member.id, data.createdBy?.id ?? null);

  if (data.isMasked) {
    return (
      <EmptyState>
        Cette fiche est masquée le temps de la dégustation à l’aveugle. La corriger
        reviendrait à la regarder.
      </EmptyState>
    );
  }

  if (!allowed) {
    return (
      <EmptyState>
        Seul l’auteur de la fiche, un maître de chai ou le fondateur peuvent la corriger.
      </EmptyState>
    );
  }

  return <EditRumForm rum={data} />;
}

function EditRumForm({ rum }: { rum: RumDetailView }): ReactNode {
  const api = useApi();
  const navigate = useNavigate();
  const fields = useRumFields(rum);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();

    const parsed = fields.build();
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // Tous les champs partent, pas seulement ceux qui ont changé : `updateRumSchema` est
      // le partiel de `createRumSchema`, et un champ vidé arrive donc à `null`, ce qui
      // l'efface. C'est ce qui permet de *retirer* une information fausse d'une fiche, et
      // pas seulement d'en ajouter — un envoi différentiel ne saurait pas distinguer
      // « inchangé » de « à effacer ».
      await api.rums.update(rum.id, parsed.value);
      void navigate(`/rhums/${rum.id}`);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={styles.page} onSubmit={(event) => void submit(event)} noValidate>
      <header>
        <Kicker>Corriger la fiche</Kicker>
        <h1>{rum.name}</h1>
        <Muted>
          Un champ vidé est effacé de la fiche. La photo se change depuis la fiche elle-même.
        </Muted>
      </header>

      <RumFields fields={fields} />

      <ErrorText>{error}</ErrorText>
      <Button type="submit" disabled={busy}>
        {busy ? 'Enregistrement…' : 'Enregistrer'}
      </Button>
      <Muted>
        <Link to={`/rhums/${rum.id}`}>Revenir à la fiche sans enregistrer</Link>
      </Muted>
    </form>
  );
}
